import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { TopicExampleModel } from '../../types/TopicModels';
import { useTopic } from '../../context/TopicContext';
import { primaryColor } from '../../constants/theme';

interface TopicExamplesModalProps {
  opened: boolean;
  onClose: () => void;
  onSelect: (example: TopicExampleModel) => void;
}

export const TopicExamplesModal = ({
  opened,
  onClose,
  onSelect,
}: TopicExamplesModalProps) => {
  const {
    examplesPage,
    isLoadingExamples,
    fetchExamples,
    fetchNextExamplesPage,
  } = useTopic();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>();

  useEffect(() => {
    if (!opened) {
      return;
    }

    fetchExamples(undefined, 1);
    setSelectedCategoryId(undefined);
  }, [fetchExamples, opened]);

  const categories = useMemo(() => {
    const categoryMap = new Map<number, string>();
    examplesPage?.items.forEach((item) => {
      if (!categoryMap.has(item.categoryId)) {
        categoryMap.set(item.categoryId, item.categoryName);
      }
    });

    return Array.from(categoryMap.entries()).map(([id, name]) => ({ id, name }));
  }, [examplesPage?.items]);

  const filtered = useMemo(() => {
    if (!examplesPage?.items) {
      return [];
    }

    if (!selectedCategoryId) {
      return examplesPage.items;
    }

    return examplesPage.items.filter((item) => item.categoryId === selectedCategoryId);
  }, [examplesPage?.items, selectedCategoryId]);

  const hasMore =
    (examplesPage?.items.length ?? 0) < (examplesPage?.totalCount ?? 0);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Topic Examples"
      centered
      size="lg"
    >
      <Stack gap="sm">
        <ScrollArea type="scroll" offsetScrollbars>
          <Group wrap="nowrap" gap={8}>
            <Badge
              radius="sm"
              variant={selectedCategoryId ? 'outline' : 'filled'}
              color={primaryColor}
              style={{ cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
              onClick={() => {
                setSelectedCategoryId(undefined);
                fetchExamples(undefined, 1);
              }}
            >
              All
            </Badge>
            {categories.map((category) => (
              <Badge
                key={category.id}
                radius="sm"
                variant={selectedCategoryId === category.id ? 'filled' : 'outline'}
                color={primaryColor}
                style={{ cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
                onClick={() => {
                  setSelectedCategoryId(category.id);
                  fetchExamples(category.id, 1);
                }}
              >
                {category.name}
              </Badge>
            ))}
          </Group>
        </ScrollArea>

        <ScrollArea h={380} type="auto" offsetScrollbars>
          <Stack gap="sm">
            {filtered.map((example) => (
              <Card
                key={example.id}
                withBorder
                radius="md"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  onSelect(example);
                  onClose();
                }}
              >
                <Stack gap={6} align="flex-start">
                  <Text c={primaryColor} fw={500} size="sm">
                    {example.categoryName}
                  </Text>
                  <Text fw={700} ta="left">{example.title}</Text>
                  {example.description && (
                    <Text c="dimmed" lineClamp={2} ta="left">
                      {example.description}
                    </Text>
                  )}
                  <Badge radius="sm" color="gray" variant="light" w="fit-content">
                    {example.suggestedDurationMinutes} mins
                  </Badge>
                </Stack>
              </Card>
            ))}
            {!isLoadingExamples && filtered.length === 0 && (
              <Text c="dimmed" ta="center" py="md">
                No examples found for this category.
              </Text>
            )}
          </Stack>
        </ScrollArea>

        {isLoadingExamples && <Loader color={primaryColor} mx="auto" size="sm" />}

        {hasMore && (
          <Button
            variant="light"
            color={primaryColor}
            onClick={() => fetchNextExamplesPage(selectedCategoryId)}
            loading={isLoadingExamples}
          >
            Load More
          </Button>
        )}
      </Stack>
    </Modal>
  );
};
